import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ChevronLeft, ChevronRight, Megaphone } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { CHANNEL_ICONS, CHANNEL_LABELS } from "../marketing/marketingDisplay";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";

type BadgeVariant = "success" | "warning" | "error" | "outline";

const STATUS_CONFIG: Record<
  API.CampaignTouch["status"],
  { label: string; variant: BadgeVariant }
> = {
  sent: { label: "Sent", variant: "success" },
  planned: { label: "Planned", variant: "outline" },
  skipped: { label: "Skipped", variant: "warning" },
  failed: { label: "Failed", variant: "error" },
};

const PAGE_SIZE = 15;

/**
 * Customer-360's "Campaign history" section — every campaign touch sent to
 * this customer's marketing contacts, and who received it, so a rep can
 * pinpoint which contacts have been reached by marketing (marketing-data-
 * visibility). Data arrives already composed on the customer-360 response
 * (`customer.campaign_history`), so this fetches nothing itself — capped to
 * the most recent 15 with a Previous/Next pager over the already-loaded
 * array, same pattern as CustomerStrategyPyramid — no separate fetch.
 */
export function CustomerCampaignHistory({
  touches,
}: {
  touches: API.CampaignTouch[];
}) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(touches.length / PAGE_SIZE));

  // A different customer's touches array replaces this one on navigation —
  // start back at page 1 rather than carrying over a stale page number.
  useEffect(() => {
    setPage(1);
  }, [touches]);

  const pageTouches = touches.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <section
      aria-label="Campaign history"
      className="rounded-lg border border-border bg-muted/30 p-4 space-y-3"
    >
      <div className="flex items-center gap-2 text-sm">
        <Megaphone className="size-4 text-muted-foreground" aria-hidden />
        <span className="font-medium">Campaign history</span>
        {touches.length > 0 && (
          <span className="text-muted-foreground">({touches.length})</span>
        )}
      </div>

      {touches.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No campaigns sent to this customer's contacts yet.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border bg-background">
          {pageTouches.map((touch) => {
            const ChannelIcon = CHANNEL_ICONS[touch.channel];
            const status = STATUS_CONFIG[touch.status];
            const sentAt = touch.executed_at ?? touch.planned_at;
            return (
              <li key={touch.id}>
                <Link
                  to={paths.marketingCampaignWorkspace({
                    campaignId: touch.campaign_id,
                  })}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-muted/50"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <ChannelIcon className="size-4 text-muted-foreground shrink-0" />
                      <span className="truncate font-medium text-foreground">
                        {touch.campaign_name}
                      </span>
                      <span className="text-muted-foreground shrink-0">
                        {CHANNEL_LABELS[touch.channel]}
                      </span>
                    </div>
                    {(touch.contact_name || touch.contact_email) && (
                      <div className="text-xs text-muted-foreground truncate">
                        To: {touch.contact_name ?? touch.contact_email}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {touch.outcome && (
                      <span className="text-muted-foreground truncate max-w-[16rem]">
                        {touch.outcome}
                      </span>
                    )}
                    <span className="text-muted-foreground whitespace-nowrap">
                      {formatDateOrDash(sentAt)}
                    </span>
                    <Badge variant={status.variant}>{status.label}</Badge>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="size-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
